import type { Prisma } from '@prisma/client'
import pickBy from 'lodash.pickby'
import type { BomRow } from './1-fileToRows'
import { castBomMetadataToMetadata, MetadataValue, MetadataSchema } from 'src/lib/metadata'
import { MapperConfig, MapperSpec, ConditionsAndExection, Condition, MapExecutionConfig } from './mapperConfigs'
import type { SelectedProject } from 'src/components/ProjectSelectCell'
import {
  MetadataType,
  PartNumberByKeyMutation,
  PartNumberByKeyMutationVariables,
  CategoryIdByPartNumberQuery,
  CategoryIdByPartNumberQueryVariables,
  Maybe,
  PartNumberGenerateMutation,
  PartNumberGenerateMutationVariables
} from 'types/graphql'
import { useMutation } from '@redwoodjs/web'
import { useLazyQuery } from '@apollo/client'
import { renderTemplate, UnchangedField, UNCHANGED_FIELD } from './lib'
import type { QuantityUnit } from 'shared/types'
import omit from 'lodash.omit'
import type { BomRowMetadata } from './1-fileToRows'
import snakeCase from 'lodash.snakecase'
import kebabCase from 'lodash.kebabcase'

export type CadSource = {
  distributorSku?: Maybe<string>
  distributorName?: Maybe<string>
  url?: Maybe<string>
}

export type ImportArtifact = {
  fileId: number
  previewUrl?: string
  filename: string
  importId: string
}

export type CompleteImportedPart = {
  hierarchy: string
  partNumber: string
  name: string
  summary: string
  cadRev: string
  quantity: number
  referenceDesignator?: string
  units: QuantityUnit
  categoryId: string
  isOffTheShelf: boolean
  metadata: Record<string, MetadataValue>
  sources: CadSource[]
  artifacts: ImportArtifact[]
}

export type ImportedPart = {
  hierarchy: string | UnchangedField
  partNumber: string
  name: string | UnchangedField
  summary: string | UnchangedField
  cadRev: string | UnchangedField
  quantity: number
  referenceDesignator?: string
  units: QuantityUnit
  categoryId: string | UnchangedField
  isOffTheShelf: boolean | UnchangedField
  metadata: Record<string, MetadataValue>
  sources: CadSource[]
  artifacts: ImportArtifact[]
  __metadata: BomRowMetadata
}

type NormalizedPart = Omit<ImportedPart, 'referenceDesignator' | 'hierarchy' | 'quantity' | 'units'>

export type CadDependency = {
  partNumber: string
  cadRev: string
  quantity: number
}

type Extra = {
  selectedProject: SelectedProject
  metadataSchema: MetadataSchema
  partNumberByKeyMutation:
    ReturnType<typeof useMutation<PartNumberByKeyMutation, PartNumberByKeyMutationVariables>>[0]
  categoryIdByPartNumberQuery:
    ReturnType<typeof useLazyQuery<CategoryIdByPartNumberQuery, CategoryIdByPartNumberQueryVariables>>[0]
  partNumberGenerationMutation:
    ReturnType<typeof useMutation<PartNumberGenerateMutation, PartNumberGenerateMutationVariables>>[0]
}

type BatchMatcher = {
  rowIndex: number
  column: string
  sourceIndex?: number
  type: 'metadata' | 'column' | 'source' | 'artifact'
  action: MapExecutionConfig
}

type SimpleBatchMatcher = {
  rowIndex: number
  column: string
  type: 'metadata' | 'column' | 'source' | 'artifact'
  action: Extract<MapExecutionConfig, {type: 'simple'} | {type: 'split'} | { type: 'regex' } | { type: 'empty' } | { type: 'quantity' } | { type: 'ignore' }>
}

type GenerateByKeyBatchMatcher = {
  rowIndex: number
  column: string
  type: 'metadata' | 'column' | 'source' | 'artifact'
  action: Extract<MapExecutionConfig, {type: 'generateByKey'}>
}

type AlwaysGenerateBatchMatcher = {
  rowIndex: number
  column: string
  type: 'metadata' | 'column' | 'source' | 'artifact'
  action: Extract<MapExecutionConfig, {type: 'alwaysGenerate'}>
}

type CategoryIdByPartNumberBatchMatcher = {
  rowIndex: number
  column: string
  type: 'metadata' | 'column' | 'source' | 'artifact'
  action: Extract<MapExecutionConfig, {type: 'categoryIdByPartNumber'}>
}

const standardizeBom = async (mapperConfig: MapperConfig, bomRows: BomRow[], extra: Extra): Promise<ImportedPart[]> => {
  const {
    metadata: metadataConfigInput,
    sources: sourcesConfigInput,
    artifacts: artifactsConfigInput,
    ...remaining
  } = mapperConfig.standardizeBom.columns
  const sourcesConfig = sourcesConfigInput || []
  const artifactsConfig = artifactsConfigInput || []
  //const metadataConfig = metadataConfigInput || {}

  let metadataConfig: Record<string, MapperSpec>
  if (mapperConfig.implicitMetadata) {
    // Create metadata config from all columns that don't start with ">"
    metadataConfig = {}

    // Collect all column names from the sample
    const allColumns = new Set<string>()
    bomRows.forEach(row => {
      Object.keys(row).forEach(key => {
        if (key !== '__metadata') {
          allColumns.add(key)
        }
      })
    })

    // Add all columns that don't start with ">" to metadata config
    allColumns.forEach(column => {
      if (!column.startsWith('>') && column.length) {
        metadataConfig[kebabCase(column)] = {
          type: 'simple',
          value: `{{row.${column}}}`
        }
      }
    })
  }
  else {
    metadataConfig = metadataConfigInput || {}
  }

  const { partNumberByKeyMutation, categoryIdByPartNumberQuery, partNumberGenerationMutation } = extra

  const matchers = bomRows.reduce((batchMatchers, row, rowIndex) => {
    const columnBatch = runMapper(row, remaining, { ...extra, rowIndex })

    const metadataBatch =  runMapper(row, metadataConfig, { ...extra, rowIndex })

    // Process sources based on configuration method
    let sourcesBatch: (Omit<BatchMatcher, 'rowIndex' | 'type'> & { sourceIndex: number })[] = []

    if (mapperConfig.autoSources) {
      // Find all columns that match the pattern >source{number}.>{property}
      const sourceColumns = Object.keys(row).filter(column => {
        return /^>source_\d+\.\>[a-zA-Z]+$/.test(column)
      })

      // Group columns by source number
      const sourceGroups: Record<number, { column: string, property: string }[]> = {}
      sourceColumns.forEach(column => {
        const match = column.match(/^>source_(\d+)\.>([a-zA-Z]+)$/)
        if (match) {
          const sourceIndex = parseInt(match[1]) - 1 // Convert to 0-based index
          const property = match[2]

          if (!sourceGroups[sourceIndex]) {
            sourceGroups[sourceIndex] = []
          }

          sourceGroups[sourceIndex].push({
            column,
            property
          })
        }
      })

      // Create batch matchers for each source property
      sourcesBatch = Object.entries(sourceGroups).flatMap(([sourceIndexStr, properties]) => {
        const sourceIndex = parseInt(sourceIndexStr)

        return properties.map(({ column, property }) => {
          // Map property names to the expected CadSource property names
          let mappedProperty: keyof CadSource
          if (property === 'sku') {
            mappedProperty = 'distributorSku'
          } else if (property === 'distributor') {
            mappedProperty = 'distributorName'
          } else if (property === 'url') {
            mappedProperty = 'url'
          } else {
            mappedProperty = property
            // Skip unknown properties
            //return []
          }

          // Only include non-empty values
          if (!row[column]) {
            return []
          }

          return {
            column: mappedProperty,
            sourceIndex,
            action: {
              type: 'simple',
              value: `${row[column]}`
            }
          }
        }).flat()
      })
    } else {
      // Use the configured sources
      sourcesBatch = sourcesConfig.flatMap((source, sourceIndex) => {
        return Object.entries(source).flatMap(([ key, sourceConfig ]) => {
          return runMapper(row, { [key]: sourceConfig }, { ...extra, rowIndex }).map(result => {
            return {
              ...result,
              sourceIndex,
            }
          })
        })
      })
    }

    const artifactsBatch = artifactsConfig.flatMap((artifact, sourceIndex) => {
      return Object.entries(artifact).flatMap(([ key, artifactConfig ]) => {
        return runMapper(row, { [key]: artifactConfig }, { ...extra, rowIndex }).map(result => {
          return {
            ...result,
            sourceIndex,
          }
        })
      })
    })

    return [
      ...batchMatchers,
      ...columnBatch.map(batch => {
        return {
          ...batch,
          rowIndex,
          type: 'column',
        } as BatchMatcher
      }),
      ...metadataBatch.map(batch => {
        return {
          ...batch,
          rowIndex,
          type: 'metadata',
        } as BatchMatcher
      }),
      ...sourcesBatch.map(batch => {
        return {
          ...batch,
          rowIndex,
          type: 'source',
        } as BatchMatcher
      }),
      ...artifactsBatch.map(batch => {
        return {
          ...batch,
          rowIndex,
          type: 'artifact',
        } as BatchMatcher
      })
    ]
  }, [] as BatchMatcher[])

  const partNumberByKeyGeneratorResults = await generatePartNumbersByKey(
    matchers.filter(m => m.action.type === 'generateByKey') as GenerateByKeyBatchMatcher[],
    partNumberByKeyMutation
  )

  const partNumberGeneratorResults = await generatePartNumbers(
    matchers.filter(m => m.action.type === 'alwaysGenerate') as AlwaysGenerateBatchMatcher[],
    partNumberGenerationMutation
  )

  const categoryIdResults = await categoryIdsByPartNumbers(
    matchers.filter(m => m.action.type === 'categoryIdByPartNumber') as CategoryIdByPartNumberBatchMatcher[],
    categoryIdByPartNumberQuery
  )

  const simpleMatchers = matchers.filter(m => {
    return m.action.type === 'simple' ||
      m.action.type === 'split' ||
      m.action.type === 'regex' ||
      m.action.type === 'quantity' ||
      m.action.type === 'ignore'
  }) as SimpleBatchMatcher[]
  const simpleMatcherResult: (BatchMatcher & { result: string })[]
    = simpleMatchers.map(matcher => {
      return {
        ...matcher,
        result: matcher.action.value || ''
      }
    })

  const allResults = [...partNumberByKeyGeneratorResults, ...categoryIdResults, ...simpleMatcherResult, ...partNumberGeneratorResults]
  const allResultsLookup = allResults.reduce((output, result) => {
    return {
      ...output,
      [result.rowIndex]: [...(output[result.rowIndex] || []), result]
    }
  }, {} as Record<string, typeof allResults[number][]>)

  let allNodes: ImportedPart[] = []
  let partsByPartNumber: Record<string, NormalizedPart> = {}

  bomRows.forEach((originalRow, i) => {
    const rowColumnActions = (allResultsLookup[i] || []).filter(m => m.type === 'column')
    const columns = rowColumnActions.reduce((columns, matcher) => {
      return {
        ...columns,
        [matcher.column]: matcher.result
      }
    }, {} as Record<keyof ImportedPart, string>)

    const rowMetadataActions = (allResultsLookup[i] || []).filter(m => m.type === 'metadata')
    const metadata = rowMetadataActions.reduce((metadata, matcher) => {
      return {
        ...metadata,
        [matcher.column]: matcher.result
      }
    }, {} as Record<string, string>)
    const rowSourceActions = (allResultsLookup[i] || []).filter(m => m.type === 'source')
    const sources = rowSourceActions.reduce((sources, matcher) => {
      let output = [...sources]
      output[matcher.sourceIndex!] = output[matcher.sourceIndex!] || {}
      output[matcher.sourceIndex!] = {
        ...output[matcher.sourceIndex!],
        [matcher.column]: matcher.result
      }
      return output
    }, [] as CadSource[]).filter(source => {
      return source.distributorName || source.distributorSku || source.url
    })

    const rowArtifactActions = (allResultsLookup[i] || []).filter(m => m.type === 'artifact')
    const artifacts = rowArtifactActions.reduce((artifacts, matcher) => {
      let output = [...artifacts]
      output[matcher.sourceIndex!] = output[matcher.sourceIndex!] || {} as Record<keyof ImportArtifact, string>
      output[matcher.sourceIndex!] = {
        ...output[matcher.sourceIndex!],
        [matcher.column]: matcher.result
      } as Record<keyof ImportArtifact, string>
      return output
    }, [] as Record<keyof ImportArtifact, string>[]).filter(source => {
      return source.fileId
    })

    // Normalizing fields that *should* be consistent between different references
    // of the same part number prevents strange behavior in the UI. Mappers can
    // choose to error in these conditions instead, but if not then we normalize
    // everything.
    const node = {
      ...castToImportedPart(columns, metadata, sources, artifacts, extra.metadataSchema),
    }

    if (!partsByPartNumber[node.partNumber]) {
      partsByPartNumber[node.partNumber] = omit(node, ['hierarchy', 'quantity', 'units', 'referenceDesignator'])
    }
    const normalizedFields = partsByPartNumber[node.partNumber]

    allNodes = [...allNodes, {
      ...node,
      ...normalizedFields,
      __metadata: originalRow.__metadata
    }]
  }, {} as Record<string, string>)

  return allNodes
}

const runMapper = (row: BomRow, mapperConfig: Record<string, MapperSpec>, extra: Extra & { rowIndex: number }) => {
  return Object.entries(mapperConfig).reduce((output, [column, config]) => {
    const possibles: ConditionsAndExection[] = Array.isArray(config) ? config : [{
      default: true,
      onMatch: config
    }]
    const spec = possibles.reduce((chosenPossible, possible) => {
      if (chosenPossible && !chosenPossible.default) return chosenPossible
      if (possible.default && chosenPossible) return chosenPossible
      if (possible.default) return possible

      if (conditionMatch(possible.conditions, row)) return possible
      return chosenPossible
    }, null as ConditionsAndExection | null)

    if (!spec) return output

    const action = renderRowMatcher(spec.onMatch, {
      row,
      project: extra.selectedProject,
      rowIndex: extra.rowIndex
    })

    return [
      ...output,
      {
        column,
        action
      }
    ]
  }, [] as Omit<BatchMatcher, 'rowIndex' | 'type'>[])
}

const rTruthy = /(true|yes)/i

const castToImportedPart = (
  input: Record<keyof ImportedPart, string>,
  metadataInput: Record<string, string>,
  sourcesInput: CadSource[],
  artifactInput: Record<keyof ImportArtifact, string>[],
  metadataSchema: MetadataSchema
) => {
  const { quantity, units, ...stringValues } = input
  const output: Omit<ImportedPart, '__metadata'> = {
    ...stringValues,
    units: units as QuantityUnit,
    quantity: Number(input.quantity),
    referenceDesignator: input.referenceDesignator || undefined,
    isOffTheShelf: (input.isOffTheShelf === UNCHANGED_FIELD) ? UNCHANGED_FIELD : rTruthy.test(input.isOffTheShelf),
    metadata: castBomMetadataToMetadata(metadataSchema, pickBy(metadataInput) as Record<MetadataType, string>),
    sources: sourcesInput,
    artifacts: artifactInput.map(a => {
      return {
        ...a,
        fileId: Number(a.fileId)
      }
    }),
  }

  return output
}

// Does an OR of all the conditions in the array
export const conditionMatch = (conditions: Condition[], row: BomRow) => {
  for (const condition of conditions) {
    if (condition.type === 'empty' && !row[condition.column]) return true
    if (condition.type === 'notEmpty' && row[condition.column]) return true
    if (condition.type === 'eq' && row[condition.column]?.toLowerCase() === condition.value?.toLowerCase()) {
      return true
    }
    if (condition.type === 'regex' && row[condition.column]) {
      const rTest = new RegExp(condition.value)
      return rTest.test(row[condition.column]!)
    }
    if (condition.type === 'contains' && row[condition.column] && row[condition.column]!.toLowerCase().includes(condition.value.toLowerCase())) {
      return true
    }
    if (condition.type === 'startsWith' && row[condition.column] && row[condition.column]!.toLowerCase().indexOf(condition.value.toLowerCase()) === 0) {
      return true
    }
  }
  return false
}

type TemplateValues = {
  row: BomRow
  rowIndex: number
  project: SelectedProject
}

type RenderRowMatcher = (config: MapExecutionConfig, values: TemplateValues) => MapExecutionConfig
const renderRowMatcher: RenderRowMatcher = (config, values) => {
  if (config.type === 'empty') return { ...config }
  if (config.type === 'ignore') return { ...config, value: UNCHANGED_FIELD }
  if (config.type === 'simple') {
    return {
      ...config,
      value: renderTemplate(config.value, values)
    }
  }
  if (config.type === 'quantity') {
    return {
      ...config,
      value: config.value
    }
  }
  if (config.type === 'split') {
    const input = renderTemplate(config.input, values)
    const parts = input.split(config.splitOn)
    const value = renderTemplate(config.value, { ...values, parts })
    return {
      ...config,
      value
    }
  }

  if (config.type === 'regex') {
    const input = renderTemplate(config.input, values)
    const rMatch = new RegExp(config.regex)
    const allParts = input.match(rMatch) || []
    const match = allParts.slice(1)
    const value = renderTemplate(config.value, { ...values, match })

    return {
      ...config,
      value
    }
  }
  if (config.type === 'generateByKey') {
    return {
      ...config,
      categoryId: renderTemplate(config.categoryId, values),
      partNumberKey: renderTemplate(config.partNumberKey, values)
    }
  }
  if (config.type === 'alwaysGenerate') {
    return {
      ...config,
      categoryId: renderTemplate(config.categoryId, values),
    }
  }
  if (config.type === 'categoryIdByPartNumber') {
    return {
      ...config,
      partNumber: renderTemplate(config.partNumber, values)
    }
  }
  throw new Error('Unrecognized config type')
}

const generatePartNumbersByKey = async (
  partGeneratorMatchers: GenerateByKeyBatchMatcher[],
  partNumberByKeyMutation: ReturnType<typeof useMutation<PartNumberByKeyMutation, PartNumberByKeyMutationVariables>>[0]
) => {
  if (partGeneratorMatchers.length === 0) return []
  const partNumberGeneratorResult = await partNumberByKeyMutation({
    variables: {
      input: {
        keyOptions: partGeneratorMatchers.map(matcher => {
          const generatorOptions = (matcher.action.generatorOptions || {}) as Prisma.JsonValue
          return {
            categoryId: matcher.action.categoryId,
            partNumberKey: matcher.action.partNumberKey,
            generatorOptions,
            schemaKey: matcher.action.schemaKey
          }
        })
      }
    }
  })

  const partNumberGeneratorResults: (BatchMatcher & { result: string })[]
    = partNumberGeneratorResult.data!.partNumberByKey.map((item, i) => {
      const matcher = partGeneratorMatchers[i]!
      return {
        ...matcher,
        result: item.partNumber
      }
    })
  return partNumberGeneratorResults
}

const generatePartNumbers = async (
  partGeneratorMatchers: AlwaysGenerateBatchMatcher[],
  partNumberGenerationMutation: ReturnType<typeof useMutation<PartNumberGenerateMutation, PartNumberGenerateMutationVariables>>[0]
) => {
  if (partGeneratorMatchers.length === 0) return []
  const partNumberGeneratorResult = await partNumberGenerationMutation({
    variables: {
      input: {
        parts: partGeneratorMatchers.map(matcher => {
          return {
            categoryId: matcher.action.categoryId
          }
        })
      }
    }
  })

  const partNumberGeneratorResults: (BatchMatcher & { result: string })[]
    = partNumberGeneratorResult.data!.generatePartNumbers.map((item, i) => {
      const matcher = partGeneratorMatchers[i]!
      return {
        ...matcher,
        result: item.partNumber
      }
    })
  return partNumberGeneratorResults
}

const categoryIdsByPartNumbers = async (
  categoryIdByPartNumberMatchers: CategoryIdByPartNumberBatchMatcher[],
  categoryIdByPartNumberQuery:
    ReturnType<typeof useLazyQuery<CategoryIdByPartNumberQuery, CategoryIdByPartNumberQueryVariables>>[0]
) => {
  if (categoryIdByPartNumberMatchers.length === 0) return []
  const categoryIdResult = await categoryIdByPartNumberQuery({
    variables: {
      input: {
        partNumbers: categoryIdByPartNumberMatchers.map(matcher => {
          return {
            partNumber: matcher.action.partNumber,
            schemaKey: matcher.action.schemaKey
          }
        })
      }
    }
  })

  const categoryIdResults: (BatchMatcher & { result: string })[]
    = categoryIdResult.data!.partCategoryByPartNumber.map((item, i) => {
      const matcher = categoryIdByPartNumberMatchers[i]!
      // if (!item.category) throw new Error(`No category found for part number ${item.partNumber}`)
      return {
        ...matcher,
        result: item.category?.id || 'other'
      }
    })
  return categoryIdResults
}

export default standardizeBom
